import Trigger from "rc-trigger";
import classnames from "classnames";
import { useState } from "react";
import { observer } from "mobx-react-lite";
import { withRouter, RouteComponentProps } from "react-router";
import {
  BoxProps,
  Center,
  Flex,
  Link,
  Spacer,
  Text,
  chakra,
  Divider,
  useMediaQuery,
} from "@chakra-ui/react";

import { getRandomHash, isUndefined, Lang } from "@noli/core";
import {
  safeUndo,
  safeRedo,
  useUndoRedoSteps,
  langStore,
  translate,
  useSafeExecute,
  useAddNode,
} from "@noli/business";

import "./index.scss";
import { genBlock } from "@/utils/bem";
import { getNewRectangle, toast, useOwnToast } from "@/utils/misc";
import { LEFT_WIDTHS } from "@/utils/constants";
import { TaskType } from "@/stores/tasks";
import { themeStore } from "@/stores/theme";
import { loginStore } from "@/modules/login/store";
import { statusStore, switchNormalBrushMode } from "@/stores/status";
import { ReactComponent as BackSvg } from "@/assets/icons/back.svg";
import { ReactComponent as LogoSvg } from "@/assets/icons/logo.svg";
import { ReactComponent as BrushSvg } from "@/assets/icons/brush.svg";
import { ReactComponent as AddTextSvg } from "@/assets/icons/add_text.svg";
import { ReactComponent as AddBlankSvg } from "@/assets/icons/add_blank.svg";

import Icon from "@/components/Icon";
import HeaderMenu from "./Menu";
import { Login_Words } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import AccelerateStatus from "./Acceleration";
import MyGadget from "@/components/bussiness/MyGadget";
import IconButton from "@/components/IconButton";
import { accelerationStore } from "@/stores/acceleration";
import { formatDuration } from "@/utils/format-time";
import { TaskPanel } from "./TaskPanel";
import { EllipseText } from "../components/styleComponents/Texts";
import { ProjectManager } from "@/modules/projects/components/ProjectManager";

const block = genBlock("c-header");
type Props = RouteComponentProps & BoxProps;

const Task = observer<{
  task: TaskType;
  color: string;
  lang: Lang;
}>(({ task: { type, status, pending, isKafka, batchId }, color, lang }) => {
  const working = isKafka ? status === "working" : pending === 0;

  if (isKafka && !batchId) {
    pending = Math.round(pending * accelerationStore.pendingFactor);
  }

  const waitTime = Math.ceil(pending / 3) * 30;
  return (
    <Flex
      w="100%"
      h="48px"
      pl="24px"
      pr="16px"
      color={color}
      fontSize="12px"
      userSelect="none"
      align="center"
      flexShrink={0}
    >
      <Text fontSize="14px" color={working ? "#3fc9a8" : color}>
        {translate(`creator-${type}-task`, lang)}{" "}
        {working ? translate(Editor_Words["creator-working-message"], lang) : ""}
      </Text>
      <Spacer />
      {working ? null : (
        <Text mr="8px" whiteSpace="nowrap" display="inline-flex" alignItems="baseline">
          {translate("pending-position", lang)} {pending}
          {waitTime ? (
            <chakra.span
              ml="5px"
              fontSize="16px"
              w="68px"
              position="relative"
              top="-2px"
              transformOrigin="left bottom"
              whiteSpace="nowrap"
              transform="scale(0.625)"
              display="inline-block"
            >
              {translate("pending-time", lang)}&nbsp;{formatDuration(waitTime)}
            </chakra.span>
          ) : null}
        </Text>
      )}
    </Flex>
  );
});

function Header(props: Props) {
  const t = useOwnToast();
  const [isSmallThan1100] = useMediaQuery(["(max-width: 1100px)"]);

  const lang = langStore.tgt;
  const { addText } = useAddNode({
    failed: async () => toast(t, "error", translate(Editor_Words["add-text-error-message"], lang)),
  });
  const { undoSteps, redoSteps } = useUndoRedoSteps();
  const undoDisabled = undoSteps === 0;
  const redoDisabled = redoSteps === 0;
  const inNormalBrushMode = statusStore.inNormalBrushMode;
  const inLandscapeMode = statusStore.inLandscapeMode;
  const { panelBg, iconColor, inputColor, captionColor } = themeStore.styles;

  const size = "64px";
  const innerSize = "32px";
  const mNormalBrush = inLandscapeMode ? "disabled" : inNormalBrushMode ? "enabled" : undefined;
  const mLandscape = inNormalBrushMode ? "disabled" : inLandscapeMode ? "enabled" : undefined;
  const getClassName = (element: string, modifier?: "enabled" | "disabled"): string => {
    if (isUndefined(modifier)) return block({ e: element });
    return classnames(block({ e: element }), block({ m: `${element}_${modifier}` }));
  };

  function addBlank(): void {
    const alias = `new.blank.${getRandomHash()}`;
    const node = getNewRectangle(alias, { autoFit: true, wh: { w: 512, h: 512 } });
    useSafeExecute("addJson", null, true, {
      failed: async () =>
        toast(t, "error", translate(Editor_Words["add-blank-error-message"], lang)),
    })({ alias, json: node.toJson() });
  }

  function addTextNode(): void {
    const alias = `new.text.${getRandomHash()}`;
    addText({ trace: true })({ alias, initColor: inputColor, lang, autoFit: true });
  }

  const logout = translate(Login_Words.logout, lang);
  return (
    <Flex
      w="100%"
      h={size}
      bg={panelBg}
      flexShrink={0}
      boxShadow="0px 4px 8px rgba(0, 0, 0, 0.15);"
      position="relative"
      zIndex={999}
      userSelect="none"
      align="center"
      {...props}
    >
      <Flex w={LEFT_WIDTHS} alignItems="center" justifyContent="space-between" h={size}>
        <Link href="/" target="_blank">
          <Icon svg={LogoSvg} squared={false} className={block({ e: "logo" })} />
        </Link>
        <Flex color={iconColor} h={innerSize} ml="34px">
          <IconButton
            icon={AddBlankSvg}
            w={innerSize}
            h={innerSize}
            mr="10px"
            onClick={addBlank}
            tip={translate(Editor_Words["header_add-blank_tip"], lang)}
            className={block({ e: "add_blank_icon" })}
          />
          <IconButton
            icon={AddTextSvg}
            w={innerSize}
            h={innerSize}
            mr="10px"
            onClick={addTextNode}
            tip={translate(Editor_Words["header_add-text_tip"], lang)}
            className={block({ e: "toolbar_icon" })}
          />

          <IconButton
            icon={BrushSvg}
            w={innerSize}
            h={innerSize}
            disabled={inLandscapeMode}
            onClick={switchNormalBrushMode}
            tip={translate(Editor_Words["header_normal-brush_tip"], lang)}
            className={block("brush_icon")}
          />

          {/* <IconButton
            icon={LandscapeSvg}
            w={iconSize}
            h={iconSize}
            mr="10px"
            disabled={inNormalBrushMode}
            onClick={switchLandscapeMode}
            tip={translate(Editor_Words["header_landscape_tip"], lang)}
            className={getClassName("toolbar_icon", mLandscape)}
          /> */}
          <Divider orientation="vertical" ml="20px" bg="#6B6B6B" borderLeftWidth="2px" />
        </Flex>
      </Flex>
      <Flex h={size} flex={1} ml="20px" py="16px">
        <Flex color={iconColor}>
          {loginStore.isGuest && <HeaderMenu />}
          <IconButton
            icon={BackSvg}
            w={innerSize}
            h={innerSize}
            mr="10px"
            disabled={undoDisabled}
            onClick={safeUndo}
            tip={translate(Editor_Words["header_undo_tip"], lang)}
            className={block({ e: "undo" })}
          />
          <IconButton
            icon={BackSvg}
            w={innerSize}
            h={innerSize}
            disabled={redoDisabled}
            onClick={safeRedo}
            mr="10px"
            tip={translate(Editor_Words["header_redo_tip"], lang)}
            className={block({ e: "redo" })}
          />
        </Flex>
        {loginStore.isGuest ? <Spacer /> : <ProjectManager />}
        <TaskPanel />
      </Flex>

      {loginStore.isGuest ? null : <AccelerateStatus />}

      <Flex userSelect="none">
        <Spacer />
        {loginStore.isGuest ? null : (
          <EllipseText
            h="32px"
            // mt="16px"
            pl="24px"
            color={captionColor}
            fontSize="14px"
            fontWeight={600}
            display="inline-flex"
            alignItems="center"
          >
            <Link href="/gallery" target="_blank">
              {translate(Editor_Words["header_link-to-gallery"], lang)}
            </Link>
          </EllipseText>
        )}

        {loginStore.isGuest ? null : (
          // <Center pr="32px">
          //   <Select
          //     options={loginStore.hasUserInfo ? [logout] : []}
          //     onOptionClick={(op) => {
          //       if (op === logout) {
          //         loginStore.logout().then(() => {
          //           props.history.replace("/login");
          //         });
          //       }
          //     }}
          //     w={9}
          //     h={9}
          //     border="none"
          //     icon={<Avatar w={9} h={9} src={loginStore.userState.userInfo.avatar} />}
          //   />
          // </Center>
          <Center pr="32px" ml="24px">
            <MyGadget openNewPageWhenJump />
          </Center>
        )}
      </Flex>
    </Flex>
  );
}

export default withRouter(observer(Header));
